import type { RasterLayerSpecification, StyleSpecification } from 'maplibre-gl';
import type { BaseLayer } from '@/stores/ui-store';

const OSM_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';
const ESRI_ATTRIBUTION = 'Imagery &copy; Esri, Maxar, Earthstar Geographics';

type RasterPaint = RasterLayerSpecification['paint'];

/**
 * Raster styles only: no API key, no billing, no vendor account.
 *
 * CARTO's basemaps — light_all, dark_all and voyager alike — now stamp
 * "API KEY REQUIRED" across every tile, so the muted and dark maps are derived
 * from OpenStreetMap tiles using raster paint properties instead. Swapping any
 * of these for a keyed vector style later is a change to this file alone.
 */
function rasterStyle(
  id: string,
  tiles: string[],
  attribution: string,
  options: { maxzoom?: number; background?: string; paint?: RasterPaint } = {},
): StyleSpecification {
  const { maxzoom = 19, background = '#eef0f4', paint } = options;

  return {
    version: 8,
    // Labels come baked into the raster tiles, so no glyph server is needed for
    // the basemap — only for our own text layers, which use this endpoint.
    glyphs: 'https://fonts.openmaptiles.org/{fontstack}/{range}.pbf',
    sources: {
      [id]: { type: 'raster', tiles, tileSize: 256, maxzoom, attribution },
    },
    layers: [
      { id: 'background', type: 'background', paint: { 'background-color': background } },
      { id, type: 'raster', source: id, paint: { 'raster-opacity': 1, ...paint } },
    ],
  };
}

const OSM_TILES = [
  'https://a.tile.openstreetmap.org/{z}/{x}/{y}.png',
  'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png',
  'https://c.tile.openstreetmap.org/{z}/{x}/{y}.png',
];

const STREET = rasterStyle('osm', OSM_TILES, OSM_ATTRIBUTION);

/** Positron-ish: desaturated and lifted so the fleet reads first. */
const MUTED_LIGHT = rasterStyle('osm-muted', OSM_TILES, OSM_ATTRIBUTION, {
  background: '#f2f4f7',
  paint: {
    'raster-saturation': -0.85,
    'raster-brightness-min': 0.22,
    'raster-contrast': -0.14,
  },
});

/**
 * Setting brightness-min above brightness-max inverts the tile's luminance
 * ramp, which turns light OSM tiles into a dark basemap. Desaturating first
 * keeps the inversion from going lurid.
 */
const MUTED_DARK = rasterStyle('osm-dark', OSM_TILES, OSM_ATTRIBUTION, {
  background: '#12141a',
  paint: {
    'raster-saturation': -0.75,
    'raster-brightness-min': 0.92,
    'raster-brightness-max': 0.06,
    'raster-contrast': 0.12,
  },
});

const SATELLITE = rasterStyle(
  'esri-imagery',
  ['https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'],
  ESRI_ATTRIBUTION,
  { maxzoom: 19, background: '#0f1720' },
);

export function mapStyleFor(layer: BaseLayer, theme: 'light' | 'dark'): StyleSpecification {
  switch (layer) {
    case 'satellite':
      return SATELLITE;
    case 'muted':
      return theme === 'dark' ? MUTED_DARK : MUTED_LIGHT;
    case 'street':
    default:
      // Street tiles are bright; at night the darkened basemap is kinder.
      return theme === 'dark' ? MUTED_DARK : STREET;
  }
}

export const BASE_LAYERS: { value: BaseLayer; label: string; hint: string }[] = [
  { value: 'street', label: 'Street', hint: 'OpenStreetMap' },
  { value: 'muted', label: 'Muted', hint: 'Low-contrast basemap' },
  { value: 'satellite', label: 'Satellite', hint: 'Esri World Imagery' },
];

/** Ghana's capital — a sane first view before any vehicle has reported. */
export const DEFAULT_CENTER: [number, number] = [-0.1969, 5.6037];
export const DEFAULT_ZOOM = 11;
